import { gql } from "graphql-request";

// Trial Queries
export const GET_TRIALS = gql`
  query GetTrials($status: TrialStatus, $organizationId: ID) {
    trials(status: $status, organizationId: $organizationId) {
      id
      userId
      organizationId
      status
      startedAt
      expiresAt
      durationMinutes
      pageViewCount
      lastActivityAt
      createdAt
      createdBy
      extensionReason
      extendedAt
      extendedBy
      convertedAt
      conversionSource
    }
  }
`;

export const GET_TRIAL = gql`
  query GetTrial($id: ID!) {
    trial(id: $id) {
      id
      userId
      organizationId
      status
      startedAt
      expiresAt
      durationMinutes
      pageViewCount
      lastActivityAt
      createdAt
      createdBy
      extensionReason
      extendedAt
      extendedBy
      convertedAt
      conversionSource
    }
  }
`;

export const GET_TRIAL_ANALYTICS = gql`
  query GetTrialAnalytics($trialId: ID!) {
    trialAnalytics(trialId: $trialId) {
      trial {
        id
        userId
        organizationId
        status
        startedAt
        expiresAt
        durationMinutes
        pageViewCount
        lastActivityAt
        createdAt
      }
      pageViews {
        id
        pagePath
        pageTitle
        viewedAt
      }
      totalViews
    }
  }
`;

// Trial Mutations
export const EXTEND_TRIAL = gql`
  mutation ExtendTrial($trialId: ID!, $input: ExtendTrialInput!) {
    extendTrial(trialId: $trialId, input: $input) {
      id
      expiresAt
      extensionReason
      extendedBy
      extendedAt
    }
  }
`;

export const CANCEL_TRIAL = gql`
  mutation CancelTrial($trialId: ID!) {
    cancelTrial(trialId: $trialId) {
      id
      status
    }
  }
`;

export const CONVERT_TRIAL = gql`
  mutation ConvertTrial($trialId: ID!, $source: String!) {
    convertTrial(trialId: $trialId, source: $source) {
      id
      status
      convertedAt
      conversionSource
    }
  }
`;

export const CREATE_TRIAL_FOR_USER = gql`
  mutation CreateTrialForUser($input: CreateTrialInput!) {
    createTrialForUser(input: $input) {
      id
      userId
      organizationId
      status
      startedAt
      expiresAt
      durationMinutes
      tempPassword
      user {
        id
        email
        fullName
      }
      organization {
        id
        name
      }
    }
  }
`;

export const RECORD_TRIAL_PAGE_VIEW = gql`
  mutation RecordTrialPageView($pagePath: String!, $pageTitle: String) {
    recordTrialPageView(pagePath: $pagePath, pageTitle: $pageTitle)
  }
`;

// TypeScript types for the GraphQL responses
export type TrialStatus = "ACTIVE" | "EXPIRED" | "CONVERTED" | "CANCELLED";

export interface Trial {
  id: string;
  userId: string;
  organizationId: string;
  status: TrialStatus;
  startedAt: string;
  expiresAt: string;
  durationMinutes: number;
  pageViewCount: number;
  lastActivityAt?: string;
  createdAt: string;
  createdBy?: string;
  extensionReason?: string;
  extendedAt?: string;
  extendedBy?: string;
  convertedAt?: string;
  conversionSource?: string;
}

export interface TrialPageView {
  id: string;
  pagePath: string;
  pageTitle?: string;
  viewedAt: string;
}

export interface TrialAnalytics {
  trial: Trial;
  pageViews: TrialPageView[];
  totalViews: number;
}

export interface GetTrialsResponse {
  trials: Trial[];
}

export interface GetTrialResponse {
  trial: Trial | null;
}

export interface GetTrialAnalyticsResponse {
  trialAnalytics: TrialAnalytics;
}

export interface ExtendTrialResponse {
  extendTrial: {
    id: string;
    expiresAt: string;
    extensionReason?: string;
    extendedBy?: string;
    extendedAt?: string;
  };
}

export interface CancelTrialResponse {
  cancelTrial: {
    id: string;
    status: TrialStatus;
  };
}

export interface ConvertTrialResponse {
  convertTrial: {
    id: string;
    status: TrialStatus;
    convertedAt?: string;
    conversionSource?: string;
  };
}

export interface ExtendTrialInput {
  additionalMinutes: number;
  reason: string;
}

export interface CreateTrialInput {
  email: string;
  fullName: string;
  organizationId: string;
  durationMinutes?: number;
}

export interface TrialUser {
  id: string;
  email: string;
  fullName: string;
}

export interface TrialOrganization {
  id: string;
  name: string;
}

export interface CreatedTrial extends Trial {
  user?: TrialUser;
  organization?: TrialOrganization;
  /** Temporary password - only returned for newly created users */
  tempPassword?: string;
}

export interface CreateTrialResponse {
  createTrialForUser: CreatedTrial;
}
